import React, { useEffect, useMemo, useState } from 'react'
import {
	Autocomplete,
	Box,
	Chip,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material'
import { AppButtonIcon, AppCardTitle, AppResultsSkeleton, AppNote } from '@components'
import { MbPage } from 'Pages/MyBusiness/_shared/Ui'

import { AdminProgress, adminCardSx, adminFieldsSx, adminHintSx, rowDeleteButtonSx, rowIconButtonSx, rowToneSx } from '../_shared/ui'

/**
 * Module 4 — Office Groups admin.
 *
 * Same two-table pattern as Extras Buckets and Pricelist Groups:
 *   1. Groups — code / name / sort_order / delete + add-row.
 *   2. Offices — one row per office with a multi-select of groups
 *      (an office can belong to several groups at once).
 *
 * Unassigned offices bubble to the top with a warning highlight so
 * they get classified first.
 */
const API = '/api/my-business/admin'

export default function OfficeGroupsPage({ embedded = false }) {
	const [groups,   setGroups]   = useState([])
	const [offices,  setOffices]  = useState([])
	const [loading,  setLoading]  = useState(true)
	const [error,    setError]    = useState(null)
	const [saving,   setSaving]   = useState(false)

	const [newCode, setNewCode] = useState('')
	const [newName, setNewName] = useState('')

	const reload = () => {
		setLoading(true); setError(null)
		Promise.all([
			fetch(`${API}/office-groups`,          { credentials: 'include' })
				.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
			fetch(`${API}/office-group-mappings`,  { credentials: 'include' })
				.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
		]).then(([g, o]) => { setGroups(g || []); setOffices(o || []) })
		  .catch(e => setError(e.message))
		  .finally(() => setLoading(false))
	}

	useEffect(() => { reload() }, [])

	const call = (path, opts = {}) => {
		setSaving(true); setError(null)
		return fetch(`${API}${path}`, {
			credentials: 'include',
			headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
			...opts,
		}).then(r => r.json().then(body => ({ ok: r.ok, status: r.status, body })))
		  .then(({ ok, status, body }) => {
			  if (!ok) throw new Error(body?.error || `HTTP ${status}`)
			  return body
		  })
		  .catch(e => { setError(e.message); throw e })
		  .finally(() => setSaving(false))
	}

	const patchGroup = (id, patch) =>
		call(`/office-groups/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
			.then(reload).catch(() => {})

	const createGroup = () => {
		const code = newCode.trim(); const name = newName.trim()
		if (!code || !name) return
		call('/office-groups', { method: 'POST', body: JSON.stringify({ code, name }) })
			.then(() => { setNewCode(''); setNewName(''); reload() })
			.catch(() => {})
	}

	const deleteGroup = (id) => {
		if (!window.confirm('Delete this group? Any office mapped to it will lose that tag.')) return
		call(`/office-groups/${id}`, { method: 'DELETE' }).then(reload).catch(() => {})
	}

	const setMapping = (office_code, group_ids) =>
		call(`/office-group-mappings/${office_code}`, {
			method: 'PUT',
			body: JSON.stringify({ group_ids }),
		}).then(reload).catch(() => {})

	// Unassigned first, then by bookings desc so the busiest offices
	// come up first inside each block.
	const ordered = useMemo(() => [...offices].sort((a, b) => {
		const au = (a.group_ids?.length || 0) === 0 ? 0 : 1
		const bu = (b.group_ids?.length || 0) === 0 ? 0 : 1
		if (au !== bu) return au - bu
		return (b.n_bookings || 0) - (a.n_bookings || 0)
	}), [offices])

	const unmappedCount = offices.filter(o => !o.group_ids || o.group_ids.length === 0).length

	const body = (
		<Box>
			<AdminProgress active={saving}/>
			{error && <AppNote severity="error" sx={{ mb: 2 }}>{error}</AppNote>}
			{loading && !offices.length ? <AppResultsSkeleton columns={4}/> : <>

				<Paper sx={{ ...adminCardSx, ...adminFieldsSx }}>
					<AppCardTitle icon="layer-group" title="Groups"/>
					<TableContainer sx={{ mt: 1.5 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell width={100}>Order</TableCell>
									<TableCell width={200}>Code</TableCell>
									<TableCell>Name</TableCell>
									<TableCell width={80}/>
								</TableRow>
							</TableHead>
							<TableBody>
								{groups.map(g => (
									<TableRow key={g.id}>
										<TableCell>
											<TextField type="number" size="small" defaultValue={g.sort_order}
											           onBlur={(e) => {
												           const v = Number(e.target.value)
												           if (v !== g.sort_order) patchGroup(g.id, { sort_order: v })
											           }}
											           inputProps={{ style: { width: 60 } }}/>
										</TableCell>
										<TableCell><code>{g.code}</code></TableCell>
										<TableCell>
											<TextField size="small" fullWidth defaultValue={g.name}
											           onBlur={(e) => {
												           const v = e.target.value.trim()
												           if (v && v !== g.name) patchGroup(g.id, { name: v })
											           }}/>
										</TableCell>
										<TableCell>
											<AppButtonIcon icon="trash" size="small" sx={rowDeleteButtonSx}
											               onClick={() => deleteGroup(g.id)}/>
										</TableCell>
									</TableRow>
								))}
								<TableRow>
									<TableCell/>
									<TableCell>
										<TextField size="small" placeholder="code (e.g. airports)" fullWidth
										           value={newCode}
										           onChange={(e) => setNewCode(e.target.value)}/>
									</TableCell>
									<TableCell>
										<TextField size="small" placeholder="Display name" fullWidth
										           value={newName}
										           onChange={(e) => setNewName(e.target.value)}/>
									</TableCell>
									<TableCell>
										<AppButtonIcon icon="plus" size="small" sx={rowIconButtonSx}
										               onClick={createGroup}
										               disabled={!newCode.trim() || !newName.trim()}/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>

				<Paper sx={{ ...adminCardSx, ...adminFieldsSx, mb: 0 }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between"
					       sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
						<AppCardTitle icon="location-dot" title={`Offices · ${unmappedCount} unassigned`}/>
						<Typography sx={adminHintSx}>
							An office can belong to several groups (Airports + South, …).
						</Typography>
					</Stack>
					<TableContainer sx={{ maxHeight: 600 }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell width={110}>Office</TableCell>
									<TableCell>Name</TableCell>
									<TableCell align="right" width={110}>Bookings 90d</TableCell>
									<TableCell width={420}>Groups</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{ordered.map(o => {
									const selected = groups.filter(g => (o.group_ids || []).includes(g.id))
									const empty = selected.length === 0
									return (
										<TableRow key={o.office_code}
										          sx={rowToneSx({ unassigned: empty })}>
											<TableCell><code>{o.office_code}</code></TableCell>
											<TableCell>{o.office_name}</TableCell>
											<TableCell align="right">
												{new Intl.NumberFormat('es-ES').format(o.n_bookings)}
											</TableCell>
											<TableCell>
												<Autocomplete multiple size="small"
												              options={groups}
												              value={selected}
												              disableCloseOnSelect
												              getOptionLabel={(g) => g.name || g.code}
												              isOptionEqualToValue={(a, b) => a.id === b.id}
												              onChange={(_, next) =>
													              setMapping(o.office_code, next.map(g => g.id))}
												              renderTags={(value, getTagProps) =>
													              value.map((g, idx) => (
														              <Chip {...getTagProps({ index: idx })}
														                    key={g.id} size="small"
														                    label={g.name || g.code}/>
													              ))}
												              renderInput={(params) => (
													              <TextField {...params} placeholder={
														              empty ? 'Assign group(s)…' : ''
													              }/>
												              )}/>
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</>}
		</Box>
	)

	if (embedded) return body
	return (
		<MbPage title="Office Groups" subtitle="Group offices for filtering">
			{body}
		</MbPage>
	)
}
